import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, TemplateRef, ViewContainerRef
} from '@angular/core';

const DEFAULT_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' }
];

@Directive({
  selector: '[appPanelTrigger]',
  exportAs: 'appPanelTrigger'
})
export class PanelTriggerDirective implements OnDestroy {

  @Input('appPanelTrigger') content!: TemplateRef<unknown>;
  @Input() appPanelPositions: ConnectedPosition[] = DEFAULT_POSITIONS;
  @Input() appPanelDismissDisabled = false;

  @Input() appPanelIgnore: string[] = [];

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private overlayRef: OverlayRef | null = null;
  private pointerDownHandler: ((e: PointerEvent) => void) | null = null;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private zone: NgZone
  ) { }

  get isOpen(): boolean {
    return !!this.overlayRef?.hasAttached();
  }

  get panelElement(): HTMLElement | null {
    return this.overlayRef?.overlayElement ?? null;
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isOpen) return;
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position()
        .flexibleConnectedTo(this.elementRef)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withPositions(this.appPanelPositions),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false
    });
    this.overlayRef.attach(new TemplatePortal(this.content, this.viewContainerRef));
    this.bindOutsideListener();
    this.opened.emit();
  }

  close(): void {
    if (!this.overlayRef) return;
    this.unbindOutsideListener();
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.closed.emit();
  }

  private bindOutsideListener(): void {
    this.pointerDownHandler = (event: PointerEvent) => {
      if (this.appPanelDismissDisabled) return;
      const path = event.composedPath();
      if (path.includes(this.elementRef.nativeElement)) return;
      if (this.overlayRef && path.includes(this.overlayRef.overlayElement)) return;
      if (this.clickedNestedPanel(path)) return;
      if (this.appPanelIgnore.length && path.some(n =>
        n instanceof Element && this.appPanelIgnore.some(sel => n.classList.contains(sel)))) return;
      this.zone.run(() => this.close());
    };
    document.addEventListener('pointerdown', this.pointerDownHandler, true);
  }

  private clickedNestedPanel(path: EventTarget[]): boolean {
    if (!this.overlayRef) return false;
    const clicked = path.find(node => node instanceof Element && node.classList.contains('cdk-overlay-pane'));
    if (!(clicked instanceof Element)) return false;
    const mine = this.overlayHost(this.overlayRef.overlayElement);
    const other = this.overlayHost(clicked);
    if (!mine || !other || mine === other) return false;
    // eslint-disable-next-line no-bitwise
    return !!(mine.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  private overlayHost(pane: Element): Element | null {
    let node: Element | null = pane;
    while (node && !node.parentElement?.classList.contains('cdk-overlay-container')) node = node.parentElement;
    return node;
  }

  private unbindOutsideListener(): void {
    if (this.pointerDownHandler) {
      document.removeEventListener('pointerdown', this.pointerDownHandler, true);
      this.pointerDownHandler = null;
    }
  }

  ngOnDestroy(): void {
    // bez emisji closed — konsumenci wieszają na niej przebudowę tabeli (prime-table:
    // onColumnVisibilityClosed), a tu jesteśmy już w trakcie niszczenia widoku
    this.unbindOutsideListener();
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
